import { Account } from "../../../../../types/apiTypes";
import { PlusIcon, TrashIcon } from "../../../../images";
import { NAME_REGEX, URL_REGEX } from "../../../../../utils/regex";
import { useEffect, useState } from "react";
import { Stack, Typography } from "@mui/material";
import { ReuseInput } from "../../../../ui";
import { CustomButton } from "../../../../ui/CustomButton";

type Props = {
  setCompanies: (companies: Account[]) => void;
  isDisabled?: boolean;
};

type AccountItem = {
  id: string;
  nameError: boolean;
  urlError: boolean;
} & Account;

export const EnterManually = ({ setCompanies, isDisabled }: Props) => {
  const [accounts, setAccounts] = useState<AccountItem[]>([]);

  useEffect(() => {
    setCompanies(
      accounts.map(({ name, size, website_url }) => ({
        name,
        size,
        website_url,
      })),
    );
  }, [accounts]);

  const addAccountHandler = () => {
    setAccounts((state) => [
      ...state,
      {
        id: Date.now().toString(),
        name: "",
        website_url: "",
        urlError: false,
        nameError: false,
      },
    ]);
  };

  const removeAccountHandler = (id: string) => {
    setAccounts(accounts.filter((account) => account.id !== id));
  };

  const changeAccountNameHandler = (item: AccountItem) => {
    if (!item.name || !NAME_REGEX.test(item.name)) {
      setAccounts(
        accounts.map((account) => {
          if (account.id === item.id)
            return {
              ...item,
              nameError: true,
            };
          return account;
        }),
      );
      return;
    }

    setAccounts(
      accounts.map((account) => {
        if (account.id === item.id) return { ...item, nameError: false };
        return account;
      }),
    );
  };

  const changeAccountUrlHandler = (item: AccountItem) => {
    if (item.website_url && !URL_REGEX.test(item.website_url)) {
      setAccounts(
        accounts.map((account) => {
          if (account.id === item.id)
            return {
              ...item,
              urlError: true,
            };
          return account;
        }),
      );
      return;
    }

    setAccounts(
      accounts.map((account) => {
        if (account.id === item.id) return { ...item, urlError: false };
        return account;
      }),
    );
  };

  const isAddAllowed = accounts.every(
    ({ name, website_url }) =>
      name.trim() &&
      NAME_REGEX.test(name) &&
      (!website_url || URL_REGEX.test(website_url)),
  );

  return (
    <Stack gap="24px">
      {accounts.length > 0 && (
        <Stack gap="16px">
          {accounts.map((item) => (
            <Stack
              key={item.id}
              flexDirection="row"
              flexWrap="nowrap"
              gap="8px"
              paddingBottom="16px"
            >
              <Stack
                paddingTop="24px"
                flexGrow={1}
                flexDirection="row"
                gap="8px"
              >
                <ReuseInput
                  isDisabled={isDisabled}
                  name="company_name"
                  handleInputChange={(field, value) =>
                    changeAccountNameHandler({ ...item, name: value })
                  }
                  errorMessage={
                    item.nameError ||
                    "Must contain only letters, numbers, and basic punctuation"
                  }
                  value={item.name || ""}
                  label="Account Name"
                  maxLength={100}
                  placeholder="Account Name"
                  isError={item.nameError}
                  setErrorHandler={() => {}}
                />
                <ReuseInput
                  name="product_url"
                  handleInputChange={(field, value) =>
                    changeAccountUrlHandler({ ...item, website_url: value })
                  }
                  errorMessage={
                    <Stack component="span" alignItems="left" width="100%">
                      <span>
                        Invalid URL: Please check the format and try again.
                      </span>
                    </Stack>
                  }
                  value={item.website_url || ""}
                  label="Account Website"
                  placeholder="https://www.example.com"
                  isError={item.urlError}
                  setErrorHandler={() => {}}
                  isDisabled={isDisabled}
                />
              </Stack>
              <Stack
                alignItems="center"
                justifyContent="center"
                paddingTop="24px"
              >
                <CustomButton
                  disabled={isDisabled}
                  variant="outlined"
                  customColor=""
                  customSize="s"
                  onClick={() => removeAccountHandler(item.id)}
                  sx={{
                    minWidth: "auto",
                    p: 1,
                    width: "42px",
                    height: "42px",
                    border: "none",
                    "&:hover": {
                      backgroundColor: "rgba(28, 60, 108, 0.08)",
                    },
                  }}
                >
                  <TrashIcon
                    color="#4C545B"
                    opacity={isDisabled ? "0.6" : "1"}
                  />
                </CustomButton>
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}

      <Stack
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        paddingBottom="16px"
      >
        {accounts.length === 0 && (
          <Typography variant="subtitle1" color="#1F293299">
            No company added yet
          </Typography>
        )}

        <CustomButton
          onClick={addAccountHandler}
          variant="outlined"
          customColor="#FFFFFF"
          textColor="#1D3E70"
          type="button"
          startIcon={
            <PlusIcon opacity={!isAddAllowed || isDisabled ? "0.6" : "1"} />
          }
          customSize="fit"
          disabled={!isAddAllowed || isDisabled}
          sx={{
            height: "32px",
            borderColor: "#1D3E70",
            padding: "8px 16px",
          }}
        >
          Add Account
        </CustomButton>
      </Stack>
    </Stack>
  );
};
